// Inputs for the DTI calculator's "Estimated monthly payment" (subject PITIA):
// which rate, which loan amount, and where each came from.

const num = (value) => {
    if (value === null || value === undefined || value === "") return 0
    const parsed = parseFloat(String(value).replace(/[,\s$]/g, ""))
    return Number.isFinite(parsed) ? parsed : 0
}

// Tenant default used when the LO has not chosen any rate yet.
// Keyed by the customer id as stored (global customer / loan_dti_results.customer).
const TENANT_DEFAULT_RATES = { mobilehomeloans: 9 }
export const FALLBACK_DTI_RATE = 7.5

export const getDefaultDTIRate = (customerId) => TENANT_DEFAULT_RATES[customerId] || FALLBACK_DTI_RATE

// Tenant default term (years) when the loan behind the rate has none.
const TENANT_DEFAULT_TERMS = { mobilehomeloans: 25 }
export const getDefaultDTITerm = (customerId) => TENANT_DEFAULT_TERMS[customerId] || 30

const loanRate = (loan) => num(loan?.base_rate ?? loan?.Base_rate ?? loan?._base_rate)
const loanHash = (loan) => loan?.hash || loan?._hash || ""

/**
 * Rate for the DTI payment, in priority order:
 *  saved     — the LO typed a rate in the calculator and clicked Done
 *  picked    — chosen in the calculator's / header's rate picker
 *  selected  — the loan selected on the Rates tab (or by the borrower)
 *  recommended — highest rate among the LO's recommended loans
 *  default   — tenant default; the calculator asks the LO to select a rate
 */
export const resolveDTIRate = (application, customerId, { ignoreSaved = false } = {}) => {
    const calc = application?.dtiCalculation || {}
    const selection = application?.selection || {}
    const selected = selection.selectedloan || {}
    const recommended = Array.isArray(selection.loSelectedLoans) ? selection.loSelectedLoans : []
    const termOf = (loan) => num(loan?.Term) || getDefaultDTITerm(customerId)

    if (!ignoreSaved && num(calc.dtiRate)) return { rate: num(calc.dtiRate), term: termOf(selected), source: "saved" }
    if (num(calc.rate)) return { rate: num(calc.rate), term: termOf(selected), source: "picked" }
    if (loanRate(selected)) return { rate: loanRate(selected), term: termOf(selected), source: "selected" }
    const hash = loanHash(selected)
    const matched = hash && recommended.find((loan) => loanHash(loan) === hash && loanRate(loan))
    if (matched) return { rate: loanRate(matched), term: termOf(matched), source: "selected" }
    const highest = recommended.reduce((best, loan) => (loanRate(loan) > loanRate(best) ? loan : best), null)
    if (highest && loanRate(highest)) return { rate: loanRate(highest), term: termOf(highest), source: "recommended" }
    return { rate: getDefaultDTIRate(customerId), term: getDefaultDTITerm(customerId), source: "default" }
}

/**
 * Principal the estimator amortizes. Refinance / cash-out use the new loan
 * amount when there is one — deriving it from "current balance − paydown"
 * gave $0 whenever the current balance was not entered in the 1003.
 */
export const estimatorPrincipal = (values) => {
    const purpose = values?.loanPurpose
    if (purpose === "purchase") {
        return num(values.propertyValue) > 0 ? num(values.propertyValue) - num(values.downpayment) : num(values.loanAmount)
    }
    if (num(values?.loanAmount) > 0) return num(values.loanAmount)
    if (purpose === "refinance") return num(values.currentLoanBalance) - num(values.paydown)
    if (purpose === "cashoutrefinance") return num(values.currentLoanBalance) + num(values.cashoutAmount)
    return 0
}

// ---- Government upfront fees and annual insurance -------------------------
// Financed upfront fees raise the loan amount the P&I is computed on; the annual
// premium is part of the monthly payment. Assumed financed, as is usual.
//
// FHA: UFMIP 1.75% of the base loan amount, financed or paid in cash (4000.1 II.A.2,
// "Financing of Upfront Mortgage Insurance Premium"). Annual MIP by term, base loan
// amount and LTV per HUD Mortgagee Letter 2023-05 (4000.1 Appendix 1.0; the chart is
// not in our guideline text, so these are not quoted from it).
export const FHA_UFMIP = 0.0175
const FHA_HIGH_BALANCE = 726200
const fhaAnnualMIP = (base, ltv, termYears) => {
    const high = base > FHA_HIGH_BALANCE
    if (termYears > 15) {
        if (high) return ltv > 0.95 ? 0.0075 : 0.007
        return ltv > 0.95 ? 0.0055 : 0.005
    }
    if (high) return ltv > 0.9 ? 0.0065 : ltv > 0.78 ? 0.004 : 0.0015
    return ltv > 0.9 ? 0.004 : 0.0015
}
// VA funding fee, regular military, first use, no exemption (VA Pamphlet 26-7 Ch. 8,
// Tables 2-4, p.219-220). Subsequent use (3.3%), Reserves, IRRRL (0.5%) and disability
// exemptions are not known from the 1003, so the payment notes the assumption.
const vaFundingFee = (purpose, downpaymentRatio) => {
    if (purpose !== "purchase") return 0.0215
    if (downpaymentRatio >= 0.1) return 0.0125
    if (downpaymentRatio >= 0.05) return 0.015
    return 0.0215
}
// USDA: 1% upfront guarantee fee (HB-1-3555 Ch. 16) and the annual fee published in
// RD Instruction 440.1 Exhibit K (0.35% of the average scheduled balance).
export const USDA_UPFRONT = 0.01
export const USDA_ANNUAL = 0.0035

/** conventional | fha | va | usda for a DTI program (fnma / fhlmc are conventional). */
export const loanTypeForProgram = (program) => (["fha", "va", "usda"].includes(program) ? program : "conventional")

/**
 * Upfront fee, total loan amount and annual insurance rate for the estimator.
 * Conventional loans have neither; their MI comes from the product or the 1003.
 */
export const governmentFees = (values) => {
    const base = Math.max(0, estimatorPrincipal(values))
    const value = num(values.propertyValue)
    const ltv = value > 0 ? base / value : 0
    const termYears = num(values.loanTerm) || 30
    const type = values.loanType || "conventional"
    let upfrontRate = 0
    let annualRate = 0
    let label = ""
    if (type === "fha") {
        upfrontRate = FHA_UFMIP
        annualRate = fhaAnnualMIP(base, ltv, termYears)
        label = "FHA"
    } else if (type === "va") {
        const down = values.loanPurpose === "purchase" && value > 0 ? num(values.downpayment) / value : 0
        upfrontRate = vaFundingFee(values.loanPurpose, down)
        label = "VA funding fee (first use)"
    } else if (type === "usda") {
        upfrontRate = USDA_UPFRONT
        annualRate = USDA_ANNUAL
        label = "USDA"
    }
    const upfront = Math.round(base * upfrontRate)
    return { base, upfrontRate, upfront, totalLoanAmount: base + upfront, annualRate, label }
}

/**
 * Monthly PITIA for estimator values: amortized P&I (interest only if flagged)
 * + taxes + insurance + HOA + PMI (PMI only above 80% LTV). Rounded like the UI.
 */
export const estimatePayment = (values) => {
    const fees = governmentFees(values)
    const principal = fees.totalLoanAmount
    const monthlyRate = num(values.interestRate) / 12 / 100
    const totalMonths = (num(values.loanTerm) || 30) * 12
    let principalAndInterest
    if (values.interestOnly) {
        principalAndInterest = principal * monthlyRate
    } else {
        // A 0% rate made the amortization factor 0/0 = NaN
        const factor = monthlyRate === 0
            ? 1 / totalMonths
            : (monthlyRate * Math.pow(1 + monthlyRate, totalMonths)) / (Math.pow(1 + monthlyRate, totalMonths) - 1)
        principalAndInterest = principal * factor
    }
    const propertyValue = num(values.propertyValue)
    const ltv = propertyValue > 0 ? fees.base / propertyValue : 0
    const type = values.loanType || "conventional"
    // MI: the selected product's (pricing) when there is one; otherwise conventional uses the
    // 1003 amount above 80% LTV, FHA / USDA the annual premium, VA none.
    let pmi = 0
    let miSource = "none"
    if (type === "va") {
        pmi = 0
    } else if ((values.pmiSource === "product" || values.pmiSource === "entered") && num(values.pmi) > 0) {
        pmi = num(values.pmi)
        miSource = values.pmiSource
    } else if (type === "fha" || type === "usda") {
        pmi = Math.round((fees.totalLoanAmount * fees.annualRate) / 12)
        miSource = "annual premium"
    } else if (ltv > 0.8) {
        pmi = num(values.pmi)
        miSource = pmi ? "1003" : "missing"
    }
    const propertyTaxes = num(values.propertyTaxes)
    const insurance = num(values.insurance)
    const hoaFees = num(values.hoaFees)
    return {
        total: Math.round(principalAndInterest + propertyTaxes + insurance + hoaFees + pmi),
        breakdown: {
            principalAndInterest: Math.round(principalAndInterest), propertyTaxes, insurance, hoaFees, pmi,
            miSource, upfrontFee: fees.upfront, upfrontRate: fees.upfrontRate, upfrontLabel: fees.label,
            totalLoanAmount: Math.round(fees.totalLoanAmount), annualMIRate: fees.annualRate,
        },
    }
}

/**
 * Initial values for EstimatedPayment, from the (legacy-shaped) application, with the
 * inputs the LO saved in the DTI settings (settings.estimator) laid over them.
 * ignoreSaved leaves out the older saved rate / interest-only columns (dtiCalculation),
 * which settings.estimator replaces.
 */
export const buildEstimatorInitialValues = (application, customerId, program, overrides, { ignoreSaved = false } = {}) =>
    applyEstimatorOverrides(estimatorBaseValues(application, customerId, program, { ignoreSaved }), overrides)

/**
 * The subject's monthly taxes, insurance, HOA and MI from the 1003 (Section 4a).
 * The split fields count only when they add up to the legacy total; otherwise
 * the whole total goes in insurance (combined: true).
 */
export const subjectExpenses1003 = (property) => {
    const propertyTaxes = num(property?.insurancetax)
    const insurance = num(property?.insurancemain)
    const hoaFees = num(property?.insurancehoa)
    const pmi = num(property?.insurancepmi)
    const legacyTotal = num(property?.insurancemaintenancetaxes)
    if (Math.abs(propertyTaxes + insurance + hoaFees + pmi - legacyTotal) >= 0.01) {
        return { propertyTaxes: 0, insurance: legacyTotal, hoaFees: 0, pmi: 0, combined: true }
    }
    return { propertyTaxes, insurance, hoaFees, pmi, combined: false }
}

const estimatorBaseValues = (application, customerId, program, { ignoreSaved }) => {
    const property = application?.property || {}
    const selection = application?.selection || {}
    const calc = application?.dtiCalculation || {}
    const purpose = property.purpose || "purchase"
    const loanSize = num(selection.loansize)
    const balance = num(application?.loanbalance)

    const propertyValue = purpose === "purchase"
        ? num(property.salesprice) || num(property.appraisal)
        : num(property.appraisal)

    let downpayment = 0
    let currentLoanBalance = 0
    let paydown = 0
    let cashoutAmount = 0
    let loanAmount = 0
    if (purpose === "purchase") {
        downpayment = loanSize > 0 && propertyValue > 0 ? propertyValue - loanSize : num(selection.downpayment)
        loanAmount = loanSize
    } else if (purpose === "refinance") {
        currentLoanBalance = balance
        paydown = num(selection.paydown)
        loanAmount = loanSize > 0 ? loanSize : Math.max(0, balance - paydown)
    } else if (purpose === "cashoutrefinance") {
        currentLoanBalance = balance
        cashoutAmount = num(selection.cashout) || (loanSize > balance && balance > 0 ? loanSize - balance : 0)
        loanAmount = loanSize > 0 ? loanSize : balance + cashoutAmount
    }

    const { propertyTaxes, insurance, hoaFees, pmi } = subjectExpenses1003(property)

    const { rate, term, source } = resolveDTIRate(application, customerId, { ignoreSaved })
    // The selected product's MI from pricing beats the 1003 amount.
    const productPmi = num(selection.selectedloan?.pmi)
    return {
        loanType: loanTypeForProgram(program),
        loanPurpose: purpose,
        propertyValue,
        interestRate: rate,
        rateSource: source,
        loanTerm: term,
        loanAmount,
        downpayment,
        currentLoanBalance,
        paydown,
        cashoutAmount,
        propertyTaxes,
        insurance,
        hoaFees,
        pmi: productPmi > 0 ? productPmi : pmi,
        pmiSource: productPmi > 0 ? "product" : "1003",
        interestOnly: !ignoreSaved && calc.interestOnly === true,
    }
}

// Estimator inputs that can be saved in settings.estimator.
export const ESTIMATOR_FIELDS = ["loanPurpose", "propertyValue", "downpayment", "loanAmount", "currentLoanBalance", "paydown",
    "cashoutAmount", "interestRate", "loanTerm", "propertyTaxes", "insurance", "hoaFees", "pmi", "interestOnly"]
const TEXT_FIELDS = new Set(["loanPurpose"])
const BOOLEAN_FIELDS = new Set(["interestOnly"])

/** base with the saved fields replaced; `overridden` lists them (for "edited here" hints). */
export const applyEstimatorOverrides = (base, overrides) => {
    const saved = overrides || {}
    const overridden = ESTIMATOR_FIELDS.filter((key) => saved[key] !== undefined && saved[key] !== null)
    if (!overridden.length) return { ...base, overridden: [] }
    const merged = { ...base, overridden }
    overridden.forEach((key) => { merged[key] = saved[key] })
    if (overridden.includes("interestRate")) merged.rateSource = "saved"
    if (overridden.includes("pmi")) merged.pmiSource = saved.source === "rateSelection" ? "product" : "entered"
    return merged
}

/** The inputs in `values` that differ from `base` — what settings.estimator stores. */
export const estimatorOverrides = (base, values) => {
    const out = {}
    ESTIMATOR_FIELDS.forEach((key) => {
        if (values?.[key] === undefined) return
        if (TEXT_FIELDS.has(key)) {
            if ((values[key] || "") !== (base[key] || "")) out[key] = values[key]
        } else if (BOOLEAN_FIELDS.has(key)) {
            if (Boolean(values[key]) !== Boolean(base[key])) out[key] = Boolean(values[key])
        } else if (num(values[key]) !== num(base[key])) {
            out[key] = num(values[key])
        }
    })
    return out
}
